import { Engine, Scene, System, SystemType, World } from 'excalibur';
import { Bird } from './bird';

/**
 * System that tracks birds within a certain range of the horizontal center of the screen.
 */
export class BirdTrackingSystem extends System {
  public readonly systemType = SystemType.Update;
  private birdsInRange: Set<Bird> = new Set();
  private range: number;
  private engine: Engine | null = null;
  private scene: Scene | null = null;

  /**
   * @param world - The Excalibur world instance
   * @param range - The horizontal distance from screen center (in pixels) to consider a bird "in range". Defaults to 200.
   */
  constructor(
    public world: World,
    range: number = 200,
  ) {
    super();
    this.range = range;
  }

  public initialize(world: World, scene: Scene): void {
    this.engine = scene.engine;
    this.scene = scene;
  }

  public update(): void {
    if (!this.engine || !this.scene) {
      return;
    }

    // Clear the previous set
    this.birdsInRange.clear();

    // Get all actors in the scene and filter for Bird instances
    const actors = this.scene.actors.filter((actor) => actor instanceof Bird && !actor.isKilled()) as Bird[];

    // Calculate screen center
    const screenCenterX = this.engine.halfCanvasWidth;

    // Check each bird's screen position
    for (const bird of actors) {
      const screenPos = this.engine.worldToScreenCoordinates(bird.pos);
      const distanceFromCenter = Math.abs(screenPos.x - screenCenterX);

      if (distanceFromCenter <= this.range) {
        this.birdsInRange.add(bird);
      }
    }
  }

  /**
   * Get all birds currently within range of the horizontal center.
   * @returns An array of Bird instances that are within range
   */
  public getBirdsInRange(): Bird[] {
    return Array.from(this.birdsInRange);
  }

  /**
   * Get the count of birds currently within range.
   * @returns The number of birds within range
   */
  public getBirdsInRangeCount(): number {
    return this.birdsInRange.size;
  }

  /**
   * Check if a specific bird is within range.
   * @param bird - The bird to check
   * @returns True if the bird is within range, false otherwise
   */
  public isBirdInRange(bird: Bird): boolean {
    return this.birdsInRange.has(bird);
  }

  /**
   * Update the range threshold.
   * @param newRange - The new range value in pixels
   */
  public setRange(newRange: number): void {
    this.range = newRange;
  }

  /**
   * Get the current range threshold.
   * @returns The current range value in pixels
   */
  public getRange(): number {
    return this.range;
  }
}
